import React, { useMemo, ComponentType } from 'react';
import { useCarousel, useCarouselPeekEffect } from './hooks';
import { useTheme, IconButton, IconButtonProps, SvgIconProps, useMediaQuery } from '@mui/material';
import type { Swiper as CarouselInstance } from 'swiper';
import { ChevronLeftIcon } from '@/src/components/icon/stroke/ChevronLeftIcon';
import { ChevronRightIcon } from '@/src/components/icon/stroke/ChevronRightIcon';
import { ArrowLeftIcon } from '@/src/components/icon/stroke/ArrowLeftIcon';
import { ArrowRightIcon } from '@/src/components/icon/stroke/ArrowRightIcon';
import { getInteractiveStyle } from '@/src/materialui/button/interactive';

export interface CarouselNavigationProps {
  /**
   * Optional Carousel instance from useCarouselInstance hook
   */
  carousel?: CarouselInstance;
  /**
   * Enable/disable peek effect on hover
   * @default: false
   */
  peekEffect?: boolean;
  /**
   * Previous Slide IconButton
   */
  prevButtonProps?: Partial<IconButtonProps>;
  /**
   * Previous icon props
   */
  prevIconProps?: SvgIconProps;
  /**
   * Previous icon node
   */
  PrevIconComponent?: ComponentType<SvgIconProps>;
  /**
   * Next Slide IconButton
   */
  nextButtonProps?: Partial<IconButtonProps>;
  /**
   * Previous icon props
   */
  nextIconProps?: SvgIconProps;
  /**
   * Next icon node
   */
  NextIconComponent?: ComponentType<SvgIconProps>;
  /**
   * Replaces Chevrons with Arrows
   * @default true
   */
  arrows?: boolean;
  /**
   * Enable if placing on a plain background (not on top of an image/video)
   * @default false
   */
  onBackground?: boolean;
  /**
   * Hide navigation buttons on case there are not enough slides for sliding
   * @default false
   */
  autoHideNavigation?: boolean;
}

/**
 * Left & Right navigation arrows
 */
export function CarouselNavigation({
  carousel: carouselProp,
  peekEffect: propPeekEffect = false,
  prevButtonProps,
  prevIconProps,
  PrevIconComponent,
  nextButtonProps,
  nextIconProps,
  NextIconComponent,
  arrows = true,
  onBackground = false,
  autoHideNavigation = false,
}: CarouselNavigationProps) {
  const carouselHook = useCarousel();
  const carousel = carouselProp || carouselHook;
  const peekEffect = carousel?.params?.effect !== 'fade' && propPeekEffect;
  const theme = useTheme();
  const upMD = useMediaQuery(theme.breakpoints.up('md'));
  const { sx: prevSx, ...restPrev } = prevButtonProps || {};
  const { sx: nextSx, ...restNext } = nextButtonProps || {};
  const [peek, setPeek] = useCarouselPeekEffect(carousel);
  const PrevIcon = PrevIconComponent || (arrows ? ArrowLeftIcon : ChevronLeftIcon);
  const NextIcon = NextIconComponent || (arrows ? ArrowRightIcon : ChevronRightIcon);

  // shared style between buttons
  const sharedSx = useMemo(
    () => ({
      // absolute inside the carousel position
      position: 'absolute',
      zIndex: '100',
      top: '50%',
      // responsive
      width: { xs: '32px', md: '40px', xl: '44px' },
      height: { xs: '32px', md: '40px', xl: '44px' },
      // manual v-align, half the icon size
      mt: { xs: '-16px', md: '-20px', xl: '-22px' },
      '& .MuiSvgIcon-root': {
        fontSize: { xs: '17px', xl: '20px' },
      },
      // highlight border
      ...getInteractiveStyle(
        {
          color: 'text.primary',
          borderColor: onBackground ? 'transparent.transparent' : 'onSurface.os100',
          backgroundColor: onBackground ? 'transparent.transparent' : 'surface.primary',
        },
        {
          backgroundColor: 'surface.secondary',
        },
      ),
      // disabled state
      '&.Mui-disabled': {
        color: 'disabled.tertiary',
        borderColor: onBackground ? 'transparent.transparent' : 'disabled.secondary',
        backgroundColor: onBackground ? 'transparent.transparent' : 'disabled.secondary',
      },
    }),
    [],
  );

  // hide navigation when not needed
  if (autoHideNavigation && carousel?.isLocked) {
    return null;
  }
  return (
    <>
      <IconButton
        disabled={carousel && !carousel.params?.loop && carousel.isBeginning}
        onClick={() => {
          carousel?.slidePrev();
          peekEffect && peek && setPeek('reset');
        }}
        onMouseEnter={() => upMD && peekEffect && setPeek('prev')}
        onMouseLeave={() => peekEffect && peek && setPeek('reset')}
        sx={{
          ...sharedSx,
          insetInlineStart: '16px',
          ...prevSx,
        }}
        aria-label="Previous slide"
        aria-controls="carousel"
        color="surface"
        size="medium"
        {...restPrev}
      >
        {theme.direction === 'rtl' ? <NextIcon {...prevIconProps} /> : <PrevIcon {...prevIconProps} />}
      </IconButton>
      <IconButton
        disabled={carousel && !carousel.params?.loop && carousel.isEnd}
        onClick={() => {
          carousel?.slideNext();
          peekEffect && peek && setPeek('reset');
        }}
        onMouseEnter={() => upMD && peekEffect && setPeek('next')}
        onMouseLeave={() => peekEffect && peek && setPeek('reset')}
        sx={{
          ...sharedSx,
          insetInlineEnd: '16px',
          ...nextSx,
        }}
        aria-label="Next slide"
        aria-controls="carousel"
        color="surface"
        {...restNext}
      >
        {theme.direction === 'rtl' ? <PrevIcon {...nextIconProps} /> : <NextIcon {...nextIconProps} />}
      </IconButton>
    </>
  );
}
